// S2 Product detail (/catalogue/products/:id) — Module 01 §4, on the
// revision-6 register.
//
// ONE page-head row (§3.2): the breadcrumb's last segment IS the title (the
// SKU), the status chip and the lifecycle actions ride its right side, and
// there is no second hero heading. The head row carries the `detail-identity`
// class because M01-S01/S13 read the SKU and the live status out of it — that
// selector is a stable contract, and restyling never rewrites a walkthrough.
//
// The tab strip and its panels live in ./products/product-detail-tabs (the
// §3.10 `NavTabs` register). The Cost tab is redaction-aware: it is only
// offered when the product response carries a cost field (§7 — a role without
// catalogue.cost.read receives the product with `standard_cost_minor` omitted,
// so the tab vanishes).

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Button } from "../../../../components/button";
import { Banner } from "../../../../components/toast";
import { CardSkeleton, ErrorState } from "../../../../components/states";
import { StatusBadge } from "../../../../components/status-badge";
import type { Product } from "../../../../lib/api";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf, RequestError } from "../../../../lib/request";
import {
  useActivateProduct,
  useDiscontinueProduct,
  useProduct,
  useReactivateProduct,
} from "../hooks/catalogue-queries";
import { useCataloguePermissions } from "../hooks/permissions";
import { ProductDetailTabs } from "./products/product-detail-tabs";
import { useQrPrint } from "./products/use-product-actions";
import "../catalogue.css";

export function ProductDetailScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams({ strict: false });
  const id = typeof params.id === "string" ? params.id : "";

  const product = useProduct(id);

  useEffect(() => {
    if (product.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [product.error, navigate]);

  if (product.isPending || product.error instanceof UnauthorizedError) {
    return (
      <section className="product-detail cat-screen">
        <CardSkeleton lines={6} label={t("catalogue.productDetail.loading")} />
      </section>
    );
  }

  if (product.error) {
    return (
      <section className="product-detail cat-screen">
        <ErrorState
          title={t("catalogue.productDetail.errorTitle")}
          detail={product.error.message}
          retryLabel={t("catalogue.state.retry")}
          onRetry={() => void product.refetch()}
          requestId={requestIdOf(product.error)}
          requestIdLabel={t("catalogue.state.requestId")}
        />
      </section>
    );
  }

  const data = product.data;

  return <ProductDetailBody product={data} />;
}

function ProductDetailBody({ product }: { product: Product }) {
  /* §1 rule 1 — see ProductDetailTabs. While an open panel has unsaved edits,
   * ITS save owns the view's one primary and the lifecycle button below steps
   * down to secondary. The screen measured two competing primaries on its
   * default landing before this. */
  const [panelDirty, setPanelDirty] = useState(false);
  return (
    <section className="product-detail cat-screen">
      <ProductHead product={product} demoted={panelDirty} />
      <ProductDetailTabs product={product} onDirtyChange={setPanelDirty} />
    </section>
  );
}

// ---- page head (identity + QR + status-gated lifecycle) ---------------------

type LifecycleAction = "activate" | "discontinue" | "reactivate";

/**
 * How many active BOMs the server says reference this product, or `undefined`
 * when the failure was anything else.
 *
 * `POST /products/:id/discontinue` answers 409 `PRODUCT_IN_ACTIVE_BOM` with a
 * `references[]` extension member (spec 01 §5; RFC 9457 extensions are merged
 * into the problem document by `problem.filter.ts`). This is the ONLY source of
 * truth for "is this product in use" available to the screen — the product
 * payload does not carry it — so the dialog asks and reads the answer rather
 * than asserting it.
 */
function activeBomRefCount(err: unknown): number | undefined {
  if (!(err instanceof RequestError)) return undefined;
  if (err.code !== "PRODUCT_IN_ACTIVE_BOM") return undefined;
  const refs = (err.problem as unknown as Record<string, unknown> | undefined)?.["references"];
  return Array.isArray(refs) ? refs.length : 0;
}

function ProductHead({ product, demoted }: { product: Product; demoted: boolean }) {
  const { t } = useTranslation();
  const qrPrint = useQrPrint();
  const perms = useCataloguePermissions();
  const activate = useActivateProduct(product.id);
  const discontinue = useDiscontinueProduct(product.id);
  const reactivate = useReactivateProduct(product.id);

  const [confirmDiscontinue, setConfirmDiscontinue] = useState(false);
  /* NOT a checkbox the user ticks up front. `null` = we have not asked the
   * server yet; a number = the server REFUSED with PRODUCT_IN_ACTIVE_BOM and
   * told us how many BOMs reference this product, which is the only moment we
   * are entitled to say so. See the dialog below. */
  const [inActiveBoms, setInActiveBoms] = useState<number | null>(null);
  /* ONE record per mutation (§3.8): a single inline banner naming what
   * happened, beside the status chip that has already flipped. The screen used
   * to keep three separate notice paragraphs — a QR line, an error line and a
   * silent status change — which is the "announced four times" noise UX-1
   * removed from onboarding. */
  const [notice, setNotice] = useState<{
    kind: "ok" | "bad";
    text: string;
    requestId?: string | undefined;
  } | null>(null);

  const busy = activate.isPending || discontinue.isPending || reactivate.isPending;

  function onError(err: unknown) {
    setNotice({
      kind: "bad",
      text: err instanceof Error ? err.message : t("catalogue.state.errorTitle"),
      requestId: requestIdOf(err),
    });
  }

  function onDone(action: LifecycleAction) {
    setNotice({ kind: "ok", text: t(`catalogue.productDetail.header.done.${action}`) });
  }

  /* Lifecycle actions are three distinct §7 codes (planner activates;
   * facility_admin/group_admin discontinue; only group_admin reactivates) —
   * gated so the UI never offers a transition the role cannot perform. The
   * server remains the authoritative gate. */
  const actions = (
    <>
      <StatusBadge status={product.status} />

      {product.qr_code_value !== null ? (
        <Button
          busy={qrPrint.isPending}
          busyLabel={t("catalogue.productDetail.header.qrPrinting")}
          onClick={() => {
            setNotice(null);
            qrPrint.mutate([product.id], {
              onError: () => {
                setNotice({
                  kind: "bad",
                  text: t("catalogue.productDetail.header.qrUnavailable"),
                });
              },
            });
          }}
        >
          {t("catalogue.productDetail.header.qrPrint")}
        </Button>
      ) : (
        <span className="cat-note">{t("catalogue.productDetail.header.qrDraft")}</span>
      )}

      {product.status === "draft" && perms.has("catalogue.product.activate") ? (
        <Button
          variant={demoted ? "secondary" : "primary"}
          busy={busy}
          busyLabel={t("catalogue.productDetail.header.working")}
          onClick={() => {
            setNotice(null);
            activate.mutate(undefined, {
              onSuccess: () => {
                onDone("activate");
              },
              onError,
            });
          }}
        >
          {t("catalogue.productDetail.header.activate")}
        </Button>
      ) : null}

      {product.status === "active" && perms.has("catalogue.product.discontinue") ? (
        <Button
          disabled={busy}
          onClick={() => {
            setConfirmDiscontinue(true);
          }}
        >
          {t("catalogue.productDetail.header.discontinue")}
        </Button>
      ) : null}

      {product.status === "discontinued" && perms.has("catalogue.product.reactivate") ? (
        <Button
          variant={demoted ? "secondary" : "primary"}
          busy={busy}
          busyLabel={t("catalogue.productDetail.header.working")}
          onClick={() => {
            setNotice(null);
            reactivate.mutate(undefined, {
              onSuccess: () => {
                onDone("reactivate");
              },
              onError,
            });
          }}
        >
          {t("catalogue.productDetail.header.reactivate")}
        </Button>
      ) : null}
    </>
  );

  return (
    <>
      {/* `.detail-identity` is an M01 selector: it must carry the SKU and the
          live status. Here it wraps the ONE page-head row, so both are inside
          it without a second heading existing on the screen. */}
      <div className="detail-identity">
        <PageHeader
          title={product.sku}
          crumbs={[
            { label: t("nav.catalogue"), to: "/catalogue/products" },
            { label: t("catalogue.productsList.title"), to: "/catalogue/products" },
          ]}
          actions={actions}
        />
      </div>
      <p className="detail-name">{product.name}</p>

      {notice !== null ? (
        <Banner
          kind={notice.kind}
          data-testid="lifecycle-result"
          dismissLabel={t("catalogue.state.dismiss")}
          onDismiss={() => {
            setNotice(null);
          }}
        >
          {notice.text}
          {notice.requestId !== undefined ? (
            <span className="state-request-id">
              {" "}
              {t("catalogue.state.requestId")}: <code>{notice.requestId}</code>
            </span>
          ) : null}
        </Banner>
      ) : null}

      {confirmDiscontinue ? (
        <div className="cat-scrim cat-scrim-center">
          <div
            className="cat-modal"
            role="dialog"
            aria-modal="true"
            aria-label={t("catalogue.productsList.discontinue.title")}
          >
            <div className="cat-modal-head">
              <h2>{t("catalogue.productsList.discontinue.title")}</h2>
            </div>
            <div className="cat-modal-body">
              <p className="cat-note cat-note-strong">
                {t("catalogue.productsList.discontinue.body")}
              </p>
              {/* Rendered only once the server has actually refused and named a
                  count. Before that this dialog makes no claim about BOM usage
                  — it used to assert "used by an active BOM" about every
                  product, including ones used by nothing. */}
              {inActiveBoms !== null ? (
                <Banner kind="warn" data-testid="discontinue-in-use">
                  {t("catalogue.productsList.discontinue.inUseFound", { count: inActiveBoms })}
                </Banner>
              ) : null}
            </div>
            <div className="cat-modal-foot">
              <div className="cat-modal-foot-right">
                <Button
                  onClick={() => {
                    setConfirmDiscontinue(false);
                    setInActiveBoms(null);
                  }}
                  disabled={busy}
                >
                  {t("catalogue.productsList.discontinue.cancel")}
                </Button>
                <Button
                  variant="primary"
                  busy={busy}
                  busyLabel={t("catalogue.productDetail.header.working")}
                  data-testid="discontinue-confirm"
                  onClick={() => {
                    setNotice(null);
                    /* Ask WITHOUT force first. The server owns the fact; if it
                     * refuses because the product is on active BOMs, we show
                     * what it told us and the same button becomes the explicit
                     * override. Two clicks, both informed. */
                    const forcing = inActiveBoms !== null;
                    discontinue.mutate(forcing ? { force: true } : {}, {
                      onSuccess: () => {
                        setConfirmDiscontinue(false);
                        setInActiveBoms(null);
                        onDone("discontinue");
                      },
                      onError: (err: unknown) => {
                        const refs = activeBomRefCount(err);
                        if (refs !== undefined) {
                          setInActiveBoms(refs);
                          return; // the dialog stays open, now telling the truth
                        }
                        onError(err);
                      },
                    });
                  }}
                >
                  {inActiveBoms !== null
                    ? t("catalogue.productsList.discontinue.force")
                    : t("catalogue.productsList.discontinue.confirm")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
